import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useAuditTrailQuery } from "../../../Services/ticket/Audit-Trail";
import { useCampignsDynamicFieldsQuery } from "../../../Services/campign/campignList";
import { useAddAttachmentsMutation } from "../../../Services/campign/CampaignDetail";
import { Loader } from "react-bootstrap-typeahead";
import DeleteAttchmentModal from "./DeleteAttchmentModal";
import ReactModal from "react-modal";
import "./AttachmentTab.scss";

interface IProps {
  ticket: any;
  refetch: any;
  uuid: any;
  refetchLeadTrail: any;
}

const AttachmentTab: React.FC<IProps> = ({
  ticket,
  refetch,
  uuid,
  refetchLeadTrail,
}) => {
  const { data: ticketDynamicFields } = useCampignsDynamicFieldsQuery({});
  const [dynamicFields, setDynamicFields] = useState<any>({});
  const [accountName, setAccountName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const { data } = useAuditTrailQuery(uuid);
  const [addAttachment] = useAddAttachmentsMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState(null);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("list");
  const [dragActive, setDragActive] = useState(false);
  const userRole = localStorage.getItem("user_role");
  const canViewAction = userRole === "admin" || userRole === "manager";

  useEffect(() => {
    if (ticket) {
      setAccountName(ticket.account_name || "");
      setFirstName(ticket.first_name || "");
      setLastName(ticket.last_name || "");
      setEmail(ticket.email || "");
      setPhoneNumber(ticket.phone_number || "");
      setDynamicFields(ticket.dynamic_fields);
    }
  }, [ticket]);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      handleFiles(event.target.files);
    }
  };

  const handleFiles = async (fileList: FileList) => {
    const newFiles = Array.from(fileList);
    const filePromises = newFiles.map((file: File) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const fileData = {
            file_name: file.name,
            file: e.target.result.split(",")[1],
          };
          resolve(fileData);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    });

    try {
      const fileDataArray = await Promise.all(filePromises);

      const updatedTicket = {
        assignee: ticket?.assignee?.id,
        assigned_by: ticket?.assignee?.username,
        status_type: ticket?.status_type,
        dynamic_fields: {},
        static_fields: {},
        new_attachments: fileDataArray,
      };

      setIsLoading(true);
      const response = await addAttachment({ variables: updatedTicket, uuid });

      if (response.data) {
        setIsLoading(false);
        toast.success("Attachment added successfully!");
        refetch();
        refetchLeadTrail();
      } else if (response.error) {
        setIsLoading(false);
        toast.error("Error: Something went wrong. Please try again.");
      }
    } catch (error) {
      setIsLoading(false);
      toast.error("An error occurred while uploading files. Please try again.");
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day} ${getMonthName(date.getMonth())} ${year}, ${hours}:${minutes}`;
  };

  const getMonthName = (month: number) => {
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return months[month];
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "webp":
        return <ImageIcon className="file-icon image" />;
      case "pdf":
        return <PictureAsPdfIcon className="file-icon pdf" />;
      case "doc":
      case "docx":
        return <DescriptionIcon className="file-icon doc" />;
      case "xls":
      case "xlsx":
      case "csv":
        return <TableChartIcon className="file-icon spreadsheet" />;
      default:
        return <InsertDriveFileIcon className="file-icon default" />;
    }
  };

  const getFileColor = (fileName: string) => {
    const extension = fileName.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "webp":
        return "file-card-grey";
      case "pdf":
        return "file-card-pink";
      case "doc":
      case "docx":
        return "file-card-blue";
      case "xls":
      case "xlsx":
      case "csv":
        return "file-card-green";
      default:
        return "file-card-grey";
    }
  };

  const handleDownload = (file: any) => {
    const link = document.createElement("a");
    link.href = file.file;
    link.download = file.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDeleteAttachment = (attachment: any) => {
    setSelectedAttachment(attachment);
    setIsModalOpen(true);
  };

  const customResetPassModalStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "450px",
      maxWidth: "90%",
      margin: "auto",
      overflow: "hidden",
      overflowY: "auto" as const,
    },
  };

  return (
    <div className="attachment-tab">
      {/* Upload Area */}
      <div
        className={`upload-area ${dragActive ? "drag-active" : ""}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="attachFileInput"
          className="file-input"
          multiple
          onChange={handleFileChange}
          accept=".xls,.xlsx,.pdf,.csv,.doc,.docx,.jpg,.jpeg,.png"
        />
        <label htmlFor="attachFileInput" className="upload-label">
          <div className="upload-icon-wrapper">
            <CloudUploadIcon className="upload-icon" />
          </div>
          <div className="upload-text">
            <p className="upload-title">Click to upload or drag and drop</p>
            <p className="upload-subtitle">
              PNG, JPG, PDF or DOCX (max. 10MB)
            </p>
          </div>
        </label>
      </div>

      {/* Files Section */}
      <div className="files-section">
        <div className="files-header">
          <h3 className="files-title">
            Files <span className="files-count">{ticket?.attachments?.length || 0}</span>
          </h3>
          <div className="view-toggle">
            <IconButton
              size="small"
              onClick={() => setViewMode("list")}
              className={viewMode === "list" ? "active" : ""}
            >
              <ViewListIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setViewMode("grid")}
              className={viewMode === "grid" ? "active" : ""}
            >
              <ViewModuleIcon />
            </IconButton>
          </div>
        </div>

        {/* Files Grid/List */}
        <div className={`files-container ${viewMode}`}>
          {ticket?.attachments?.map((attachment: any, index: number) => (
            <div key={index} className={`file-card ${getFileColor(attachment.file_name)}`}>
              <div className="file-preview">
                {getFileIcon(attachment.file_name)}
              </div>
              <div className="file-info">
                <Tooltip title={attachment.file_name}>
                  <h4 className="file-name">{attachment.file_name}</h4>
                </Tooltip>
                <p className="file-date">{formatDate(attachment.created)}</p>
              </div>
              <div className="file-footer">
                <span className="file-size">
                  {attachment.file_size ? formatFileSize(attachment.file_size) : ""}
                </span>
                <div className="file-actions">
                  <Tooltip title="Download">
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(attachment)}
                      className="action-btn download-btn"
                    >
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canViewAction && (
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAttachment(attachment)}
                        className="action-btn delete-btn"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {ticket?.attachments?.length === 0 && (
          <div className="no-files">
            <InsertDriveFileIcon className="no-files-icon" />
            <p>No attachments yet</p>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      <ReactModal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={customResetPassModalStyles}
        contentLabel="Delete Attachment"
        shouldCloseOnOverlayClick={false}
      >
        <DeleteAttchmentModal
          selectedAttachment={selectedAttachment}
          closeDeleteAttachmentModal={() => setIsModalOpen(false)}
          refetch={refetch}
          refetchLeadTrail={refetchLeadTrail}
          assignee={ticket?.assignee?.id}
          assigned_by={ticket?.assignee?.username}
          status_type={ticket?.status_type}
        />
      </ReactModal>

      {isLoading && (
        <div className="loader-overlay">
          <Loader />
        </div>
      )}
    </div>
  );
};

export default AttachmentTab;